if (!customElements.get('fp-description-preview')) {
  class FPDescriptionPreview extends HTMLElement {
    connectedCallback() {
      if (this.initialized || !window.Intl?.Segmenter) return;
      this.full = this.querySelector('[data-fp-description-full]');
      this.short = this.querySelector('[data-fp-description-short]');
      this.button = this.querySelector('button');
      if (!this.full || !this.short || !this.button) return;
      if ([...this.full.querySelectorAll('*')].some(el => !['P', 'BR', 'STRONG', 'EM', 'B', 'I', 'A', 'SPAN', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI', 'HR', 'META'].includes(el.tagName))) return;
      this.initialized = true;
      this.expanded = false;
      this.onClick = () => {
        this.expanded = !this.expanded;
        this.updateVisibility();
        if (!this.expanded) {
          this.button.focus({ preventScroll: true });
          const rect = this.button.getBoundingClientRect();
          const header = document.querySelector('.section-header-sticky');
          const top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
          if (rect.top < top || rect.bottom > window.innerHeight) {
            this.button.scrollIntoView({ block: 'center', behavior: 'auto' });
          }
        }
      };
      this.button.addEventListener('click', this.onClick);
      this.schedule = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.measure());
      };
      this.media = window.matchMedia('(max-width: 749px)');
      this.media.addEventListener('change', this.schedule);
      this.resize = new ResizeObserver(entries => {
        const width = entries[0].contentRect.width;
        if (width > 0 && width !== this.lastWidth) {
          this.lastWidth = width;
          this.schedule();
        }
      });
      this.resize.observe(this);
      this.accordion = this.closest('collapsible-expandable');
      if (this.accordion) {
        this.observer = new MutationObserver(this.schedule);
        this.observer.observe(this.accordion, { attributes: true, attributeFilter: ['expanded'] });
      }
      document.fonts?.ready.then(() => { if (this.isConnected) this.schedule(); });
      this.schedule();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.resize?.disconnect();
      this.observer?.disconnect();
      this.media?.removeEventListener('change', this.schedule);
      this.button?.removeEventListener('click', this.onClick);
      this.initialized = false;
    }

    lineCount(element) {
      const tops = [];
      const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        if (!walker.currentNode.textContent.trim()) continue;
        const range = document.createRange();
        range.selectNodeContents(walker.currentNode);
        for (const rect of range.getClientRects()) {
          if (rect.width > 0 && rect.height > 0 && !tops.some(top => Math.abs(top - rect.top) < 2)) tops.push(rect.top);
        }
      }
      return tops.length;
    }

    fragmentThrough(end) {
      const walker = document.createTreeWalker(this.full, NodeFilter.SHOW_TEXT);
      const range = document.createRange();
      range.selectNodeContents(this.full);
      let offset = 0;
      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (offset + node.length >= end) {
          range.setEnd(node, end - offset);
          const fragment = range.cloneContents();
          fragment.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
          return fragment;
        }
        offset += node.length;
      }
      return this.full.cloneNode(true);
    }

    measure() {
      if (!this.isConnected || this.getBoundingClientRect().width === 0) return;
      if (this.accordion?.getAttribute('expanded') === 'false') return;
      const text = this.full.textContent;
      const segmenter = new Intl.Segmenter(this.lang || 'de', { granularity: 'sentence' });
      const textBlocks = [...this.full.querySelectorAll('p, li')].filter(el => !el.querySelector('p, li'));
      const blocks = textBlocks.length ? textBlocks : [this.full];
      const ends = blocks.flatMap(block => {
        const prefix = document.createRange();
        prefix.selectNodeContents(this.full);
        prefix.setEnd(block, 0);
        const offset = prefix.toString().length;
        return [...segmenter.segment(block.textContent)]
          .filter(part => part.segment.trim())
          .map(part => offset + part.index + part.segment.trimEnd().length);
      });
      if (!ends.length) return;
      const limit = this.media.matches ? 8 : 6;
      this.short.classList.add('fp-description-preview__measure');
      this.short.setAttribute('aria-hidden', 'true');
      this.short.inert = true;
      this.short.hidden = false;
      let chosen = ends[0];
      for (const end of ends.slice(0, 4)) {
        this.short.replaceChildren(this.fragmentThrough(end));
        if (this.lineCount(this.short) > limit) break;
        chosen = end;
      }
      this.truncated = text.slice(chosen).trim().length > 0;
      this.short.replaceChildren(this.fragmentThrough(chosen));
      this.short.classList.remove('fp-description-preview__measure');
      this.short.removeAttribute('aria-hidden');
      this.short.inert = false;
      this.updateVisibility();
    }

    updateVisibility() {
      const showFull = !this.truncated || this.expanded;
      this.full.hidden = !showFull;
      this.short.hidden = showFull;
      this.button.hidden = !this.truncated;
      this.button.setAttribute('aria-expanded', String(showFull));
      this.button.textContent = showFull ? this.button.dataset.less : this.button.dataset.more;
    }
  }
  customElements.define('fp-description-preview', FPDescriptionPreview);
}
